import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, Snackbar } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

const FIREBASE_DB_URL = 'https://armour-pro-2a88a-default-rtdb.firebaseio.com';

const LoginForm = ({ onSignupSuccess }) => {
  const [formData, setFormData] = useState({ email: '', password: '', name: '', isSignup: false });
  const [errors, setErrors] = useState({ email: '', password: '', name: '', general: '' });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '', general: '' });
  };

  const validateForm = () => {
    let tempErrors = { name: '', email: '', password: '', general: '' };
    let isValid = true;
    if (formData.isSignup && !formData.name.trim()) {
      tempErrors.name = 'Full Name is required';
      isValid = false;
    }
    if (!formData.email.trim()) {
      tempErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = 'Email is invalid';
      isValid = false;
    }
    if (!formData.password) {
      tempErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 6) {
      tempErrors.password = 'Password must be at least 6 characters';
      isValid = false;
    }
    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.isSignup && !validateForm()) return;

    setLoading(true);
    try {
      if (formData.isSignup) {
        // Signup logic
        const response = await fetch(`${FIREBASE_DB_URL}/users.json`);
        const users = await response.json();
        const userCount = users ? Object.keys(users).length : 0;
        const nextId = (userCount + 1).toString().padStart(3, '0');

        const userData = {
          name: formData.name,
          email: formData.email,
          password: formData.password, // Note: In production, hash passwords
          role: 'user',
          createdAt: new Date().toISOString(),
        };

        await fetch(`${FIREBASE_DB_URL}/users/${nextId}.json`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(userData),
        });

        setSnackbar({ open: true, message: 'Registration successful! Please login.' });
        setFormData({ ...formData, isSignup: false, name: '', password: '' });
        if (onSignupSuccess) onSignupSuccess();
      } else {
        // Login logic
        if (formData.email === 'admin@gmail.com' && formData.password === 'admin123') {
          localStorage.setItem('userData', JSON.stringify({ email: formData.email, role: 'admin', name: 'Admin' }));
          navigate('/dashboard');
          return;
        }

        const response = await fetch(`${FIREBASE_DB_URL}/users.json`);
        const users = await response.json();
        if (!users) {
          setErrors({ ...errors, general: 'No users found' });
          setLoading(false);
          return;
        }

        const user = Object.values(users).find(
          (u) => u.email === formData.email && u.password === formData.password
        );
        if (user) {
          localStorage.setItem('userData', JSON.stringify({ ...user, role: 'user' }));
          navigate('/dashboard');
        } else {
          setErrors({ ...errors, general: 'Invalid email or password' });
        }
      }
    } catch (error) {
      setErrors({ ...errors, general: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box sx={{ bgcolor: 'white', p: 4, borderRadius: 2, width: '100%', maxWidth: 400, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
        <Typography variant="h4" sx={{ mb: 3, color: '#1e40af', textAlign: 'center', fontWeight: 'bold' }}>
          {formData.isSignup ? 'Sign Up' : 'Login'}
        </Typography>
        <form onSubmit={handleSubmit}>
          {formData.isSignup && (
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1e40af' }, '&:hover fieldset': { borderColor: '#1e88e5' }, '&.Mui-focused fieldset': { borderColor: '#1e40af' } } }}
            />
          )}
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email || !!errors.general}
            helperText={errors.email || errors.general}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1e40af' }, '&:hover fieldset': { borderColor: '#1e88e5' }, '&.Mui-focused fieldset': { borderColor: '#1e40af' } } }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password || !!errors.general}
            helperText={errors.password || errors.general}
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1e40af' }, '&:hover fieldset': { borderColor: '#1e88e5' }, '&.Mui-focused fieldset': { borderColor: '#1e40af' } } }}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: '#1e40af', color: 'white', '&:hover': { bgcolor: '#1e88e5' }, py: 1.5 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : formData.isSignup ? 'Register' : 'Login'}
          </Button>
        </form>
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          {formData.isSignup ? (
            <>Already have an account? <Link to="#" onClick={() => setFormData({ ...formData, isSignup: false })} style={{ color: '#1e40af' }}>Login</Link></>
          ) : (
            <>Don't have an account? <Link to="#" onClick={() => setFormData({ ...formData, isSignup: true })} style={{ color: '#1e40af' }}>Sign Up</Link></>
          )}
        </Typography>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Box>
    </Box>
  );
};

export default LoginForm;

